import { type AxePurchaseState } from '@deque/billing-service-client';
import { getAllUserSubscriptions } from '@deque/billing-utils';
import { type TFunction } from 'i18next';
import { useMemo } from 'react';

import { useAuthContext } from '~/common/contexts/auth';
import { useEnterprises } from '~/common/contexts/enterprises';
import { useProducts } from '~/common/contexts/products';

export interface ProductAccessRow {
  /** Subscription id — stable, unique React key. */
  id: string;
  slug: string;
  /** Resolved product name, or a humanized slug fallback. */
  name: string;
  /** Raw `AxePurchaseState`; map to a label with `purchaseStateLabel`. */
  state: AxePurchaseState;
  /** Whether the slug resolved to a customer-facing product. */
  recognized: boolean;
}

export interface EnterpriseRow {
  id: string;
  name: string;
}

export interface SupportInfo {
  keycloakId?: string;
  activeEnterprise: { id: string; name: string } | null;
  isEnterpriseAdmin: boolean;
  products: ProductAccessRow[];
  enterprises: EnterpriseRow[];
  loading: boolean;
}

/** Turn a slug or raw state into Title Case words (e.g. `scim-service` -> `Scim Service`). */
export const humanizeSlug = (slug: string): string =>
  slug
    .split(/[-_]/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/** Map an `AxePurchaseState` to a translated, human-friendly label. */
export const purchaseStateLabel = (state: string, t: TFunction): string => {
  switch (state) {
    case 'paid':
      return t('Paid');
    case 'trialing':
      return t('Trial');
    case 'trial_ended':
      return t('Trial ended');
    case 'trial_payment_failed':
      return t('Trial (payment failed)');
    case 'paid_payment_failed':
      return t('Paid (payment failed)');
    case 'free':
      return t('Free');
    case 'free_payment_failed':
      return t('Free (payment failed)');
    case 'none':
      return t('No access');
    default:
      return humanizeSlug(state);
  }
};

/**
 * Assembles the (already client-side) account/identity data shown in the
 * Support Info drawer: Keycloak id, active enterprise, admin status, product
 * subscriptions, and enterprise memberships. No tokens or other secrets are
 * included.
 */
export const useSupportInfo = (): SupportInfo => {
  const { user, billingUser, loading: authLoading } = useAuthContext();
  const {
    activeEnterprise,
    enterprises,
    isAdmin,
    loading: enterprisesLoading
  } = useEnterprises();
  const { getProductBySlug, loading: productsLoading } = useProducts();

  const products = useMemo<ProductAccessRow[]>(() => {
    if (!billingUser) {
      return [];
    }
    // Personal and enterprise subscriptions are mutually exclusive in practice:
    // if the user belongs to any enterprise, show only the active enterprise's
    // subscriptions. 'active-only' scopes to the active enterprise (the app
    // treats enterprises[0] as active). We read raw subscriptions (not the
    // deprecated `listProductAccess`) so all purchase states show.
    const subscriptions = getAllUserSubscriptions(billingUser, 'active-only');
    const relevant =
      billingUser.enterprises.length > 0
        ? subscriptions.filter(subscription => 'enterprise_id' in subscription)
        : subscriptions;
    return relevant.map(subscription => {
      const product = getProductBySlug(subscription.product_slug);
      return {
        id: subscription.id,
        slug: subscription.product_slug,
        name: product?.name ?? humanizeSlug(subscription.product_slug),
        state: subscription.purchase_state as AxePurchaseState,
        recognized: Boolean(product)
      };
    });
  }, [billingUser, getProductBySlug]);

  return {
    keycloakId: user?.id,
    activeEnterprise: activeEnterprise
      ? { id: activeEnterprise.id, name: activeEnterprise.name }
      : null,
    isEnterpriseAdmin: isAdmin,
    products,
    enterprises: enterprises.map(enterprise => ({
      id: enterprise.id,
      name: enterprise.name
    })),
    loading: authLoading || enterprisesLoading || productsLoading
  };
};
